/**
 * registry/resolution.ts — ВОЗОБНОВЛЯЕМАЯ резолюция: драйвер дренажа очереди с
 * поддержкой хуков-генераторов (choose через континуации, R2/D15, tech/21 §5.1).
 *
 * Центральная работа M3b. Обычный дренаж (tech/21 §5) снимает события FIFO и на
 * каждое зовёт подписчиков в каноническом порядке. Здесь подписчик может быть
 * ГЕНЕРАТОРОМ: на `yield ctx.choose(req)` его живой итератор ПРИОСТАНАВЛИВАЕТСЯ,
 * в состояние пишется СЕРИАЛИЗУЕМЫЙ pendingChoice, а шаг возвращает
 * status:'awaiting_choice', НИЧЕГО больше не применяя.
 *
 * ФОРМА «ПРИОСТАНОВЛЕННОГО ШАГА» (Suspension) — ТРАНЗИЕНТНЫЙ объект В ПАМЯТИ:
 * живой итератор + позиция дренажа (frame: текущее событие + оставшиеся
 * подписчики) + очередь + накопленные события + черновик. В GameState — ТОЛЬКО
 * pendingChoice (чистый JSON). Резолюция НЕ сериализуется; при реплее
 * воссоздаётся детерминированно из начального состояния + журнала Action
 * (включая Resolve) — генератор повторно приходит в ту же точку yield.
 *
 * ЕДИНСТВЕННЫЙ pendingChoice: дренаж строго последователен, активный генератор
 * всегда один ⇒ несколько choose (в т.ч. у разных карт) сериализуются через ОДИН
 * слот в каноническом порядке подписчиков (вторая карта ждёт Resolve первой).
 *
 * GC эффектов (D21/R8): после кадра события, снявшего носителя/источника
 * (UnitDied/UnitMoved/CardMoved-с-поля), прогоняется sweep — снятие через
 * ops.removeEffect (EffectRemoved → очередь → каскад хукам, детерминированно).
 */
import { buildCardContext } from '../ctx/context.js';
import { sweepEffects } from '../effects/gc.js';
import { hasOngoingProviders, reconcileOngoing } from '../effects/ongoing.js';
import { EventQueue } from '../events/queue.js';
import { applyEvent } from '../events/reducer.js';
import { EVENT_LIMIT_PER_STEP, finalizeDeadUnits } from '../events/runStep.js';
import { compareByCanonicalPlacement } from '../hooks/hooks.js';
import { bindOps } from '../ops/ops.js';
import { toUnitView } from '../ctx/views.js';
import { makeReplacementDeps } from './replacements.js';
import { collectSubscribers, collectPreApplySubscribers, invokeHookBody, isLiveUnit, isGenerator, } from './subscribers.js';
import { buildPublicTargetContext } from '../targeting/publicContext.js';
import { materializeChoice, pendingChoiceOf, resolveChoiceTokens } from '../targeting/choice.js';
import { markAbilityBoundaryFizzled } from '../targeting/fizzleBoundary.js';
/** Событие сняло носителя/источник эффекта ⇒ нужен GC-sweep (D21/R8). */
function isCarrierRemovingEvent(event) {
    if (event.type === 'UnitDied' || event.type === 'UnitMoved' || event.type === 'CardConsumed')
        return true;
    return event.type === 'CardMoved' && event.from === 'board' && event.to !== 'board';
}
/** Собрать транзиентную резолюцию поверх копии состояния. */
function makeSuspension(registry, draft, queue, workflow = null) {
    const presentationCause = { value: undefined };
    const isPublicVar = (target, key) => {
        const defId = target.kind === 'unit'
            ? draft.instances[target.instanceId]?.defId
            : target.kind === 'effect'
                ? draft.effects.find((effect) => effect.id === target.effectId)?.defId
                : draft.attachments.find((attachment) => attachment.attachmentId === target.attachmentId)?.defId;
        return defId !== undefined && registry.get(defId)?.publicVarKeys?.includes(key) === true;
    };
    const baseOps = bindOps(draft, queue, {
        ...makeReplacementDeps(registry),
        isCardMoveBlocked: (instanceId) => workflow?.isCardMoveBlocked?.(instanceId) === true,
        isCellReserved: (cellId) => workflow?.isCellReserved?.(cellId) === true,
        isPendingFieldReceiver: (instanceId) => workflow?.isPendingFieldReceiver?.(instanceId) === true,
        presentationCauseEventIndex: () => presentationCause.value,
        isPublicVar,
    });
    const suspension = {
        registry,
        draft,
        queue,
        events: [],
        baseOps,
        frame: null,
        nextChoiceSeq: 1,
        active: null,
        terminalError: null,
        orchestrationFrame: null,
        workflow,
        subscriberSnapshots: new Map(),
        presentationCause,
        ownPlansByEvent: new Map(),
        tokenAppearanceSnapshots: new Map(),
        pendingTokenAppearanceMarkers: new Set(),
        hasOngoing: hasOngoingProviders(registry),
    };
    const preSnapshots = new Map();
    queue.setBeforeApplyObserver((event) => {
        preSnapshots.set(event, collectPreApplySubscribers(draft, registry, event));
    });
    queue.setEnqueueObserver((event) => {
        if (event.type === 'UnitEnteredPlay' && event.cause === 'token') {
            const instance = draft.instances[event.instanceId];
            const cell = draft.board.cells[event.cellId];
            if (instance !== undefined && cell !== undefined) {
                suspension.tokenAppearanceSnapshots.set(event, {
                    instanceId: instance.id,
                    defId: instance.defId,
                    ownerId: instance.ownerId,
                    cellId: event.cellId,
                    tableId: cell.tableId,
                });
            }
        }
        const ownPlan = suspension.ownPlansByEvent.get(event);
        suspension.ownPlansByEvent.delete(event);
        const subs = [
            ...(preSnapshots.get(event) ?? []),
            ...collectSubscribers(draft, registry, event),
        ].map((subscriber) => {
            if (ownPlan !== undefined
                && event.type === 'UnitEnteredPlay'
                && subscriber.hookName === 'onEnterPlay'
                && 'selfId' in subscriber.subject
                && subscriber.subject.selfId === event.instanceId) {
                return { ...subscriber, plan: structuredClone(ownPlan) };
            }
            return subscriber;
        });
        preSnapshots.delete(event);
        subs.sort(compareByCanonicalPlacement);
        suspension.subscriberSnapshots.set(event, subs);
    });
    return suspension;
}
/** Сериализуемый дескриптор запроса (filter НЕ переносится — он в живом генераторе). */
function toLegacyPendingChoice(choiceId, request, candidates) {
    const requestData = {
        kind: request.kind,
        count: request.count,
        ...(request.candidates !== undefined ? {
            // Never echo hidden/invalid auth ids: only the already-public materialized domain.
            candidates: candidates.flatMap(({ candidate }) => candidate.kind === 'unit' || candidate.kind === 'card' ? [candidate.instanceId] : []),
        } : {}),
    };
    return {
        choiceId,
        playerId: request.who,
        cardinality: { min: request.count, max: request.count },
        ordered: true,
        candidates: candidates.map((candidate) => structuredClone(candidate)),
        request: requestData,
    };
}
/** Валидация формы запроса выбора (карта могла собрать мусор). */
function validateChooseRequest(request) {
    if (typeof request.who !== 'string' || request.who === '') {
        throw new Error('ctx.choose: who (playerId) должен быть непустой строкой');
    }
    if ('spec' in request)
        return;
    if (typeof request.kind !== 'string' || request.kind === '') {
        throw new Error('ctx.choose: kind должен быть непустой строкой');
    }
    if (!Number.isInteger(request.count) || request.count < 0) {
        throw new Error(`ctx.choose: count должен быть неотрицательным целым (получено ${String(request.count)})`);
    }
    if (request.candidates !== undefined && !Array.isArray(request.candidates)) {
        throw new Error('ctx.choose: candidates должен быть массивом id');
    }
}
function isTargetChooseRequest(request) {
    return 'spec' in request;
}
function legacyCandidateDomain(draft, request) {
    const ids = request.candidates ?? Object.values(draft.instances)
        .filter((instance) => instance.zone === 'board' && instance.cellId !== undefined)
        .sort((a, b) => a.seq - b.seq || a.id.localeCompare(b.id))
        .map((instance) => instance.id);
    const out = [];
    for (const id of ids) {
        const instance = draft.instances[id];
        if (instance === undefined)
            continue;
        if (instance.zone === 'board' && instance.cellId !== undefined) {
            if (request.filter !== undefined && !request.filter(toUnitView(instance)))
                continue;
            out.push({ kind: 'unit', instanceId: id });
        }
        else if (instance.ownerId === request.who
            && (instance.zone === 'hand' || instance.zone === 'discard' || instance.zone === 'attachment')) {
            out.push({ kind: 'card', instanceId: id, zone: instance.zone });
        }
    }
    return out;
}
/**
 * Валидация присланного selection по ЖИВОМУ запросу (R2): число целей = count;
 * каждая — в candidates (если заданы) и проходит filter (если задан). Сервер
 * авторитетно отклоняет мусор — бросает понятную ошибку, состояние не трогается.
 */
function validateSelection(draft, request, selection) {
    const where = 'Resolve';
    if (!Array.isArray(selection.picked)) {
        throw new Error(`${where}: selection.picked должен быть массивом id`);
    }
    const picked = selection.picked;
    if (picked.length !== request.count) {
        throw new Error(`${where}: выбрано ${picked.length} целей, требуется ${request.count} (kind '${request.kind}')`);
    }
    if (request.candidates !== undefined) {
        for (const id of picked) {
            if (!request.candidates.includes(id)) {
                throw new Error(`${where}: цель "${id}" вне списка кандидатов запроса`);
            }
        }
    }
    const filter = request.filter;
    if (filter !== undefined) {
        for (const id of picked) {
            const inst = draft.instances[id];
            if (inst === undefined) {
                throw new Error(`${where}: цель "${id}" не существует`);
            }
            if (!filter(toUnitView(inst))) {
                throw new Error(`${where}: цель "${id}" не проходит фильтр запроса`);
            }
        }
    }
}
/** Приостановить резолюцию на данном итераторе+запросе: пишет pendingChoice. */
function suspendOn(s, iterator, request) {
    validateChooseRequest(request);
    const choiceId = `c${s.nextChoiceSeq}`;
    s.nextChoiceSeq += 1;
    if (isTargetChooseRequest(request)) {
        const materialized = materializeChoice(choiceId, request, buildPublicTargetContext(s.draft, request.who, s.registry));
        if (materialized.status === 'match_error') {
            s.terminalError = materialized.reason;
            return 'match_error';
        }
        if (materialized.status !== 'pending')
            return materialized.status;
        s.active = {
            iterator, choiceId, request, descriptor: materialized.descriptor,
            ...(s.presentationCause.value === undefined
                ? {}
                : { causeEventIndex: s.presentationCause.value }),
        };
        s.draft.pendingChoice = pendingChoiceOf(materialized.descriptor, request.prompt);
        return 'pending';
    }
    const domain = legacyCandidateDomain(s.draft, request);
    if (domain.length < request.count) {
        s.terminalError = `legacy choice "${choiceId}" имеет ${domain.length} кандидатов при count=${request.count}`;
        return 'match_error';
    }
    const candidates = domain.map((candidate, index) => ({
        choiceToken: `${choiceId}.t${index.toString(36)}`,
        candidate,
    }));
    const targetByToken = new Map(candidates.map(({ choiceToken, candidate }) => [choiceToken, candidate]));
    const descriptor = {
        choiceId,
        playerId: request.who,
        cardinality: { min: request.count, max: request.count },
        ordered: true,
        candidates,
        targetByToken,
        domain,
    };
    s.active = {
        iterator, choiceId, request, descriptor,
        ...(s.presentationCause.value === undefined
            ? {}
            : { causeEventIndex: s.presentationCause.value }),
    };
    s.draft.pendingChoice = toLegacyPendingChoice(choiceId, request, candidates);
    return 'pending';
}
/** Собрать результат awaiting_choice из текущего pendingChoice. */
function awaitingResult(s) {
    const pendingChoice = s.draft.pendingChoice;
    if (pendingChoice === undefined) {
        throw new Error('resolution: awaiting без pendingChoice (внутренняя ошибка драйвера)');
    }
    return { status: 'awaiting_choice', state: s.draft, events: s.events, pendingChoice, suspension: s };
}
function matchError(s, reason) {
    return {
        status: 'match_error',
        state: s.draft,
        events: s.events,
        reason: reason ??
            `превышен лимит событий на шаг (${EVENT_LIMIT_PER_STEP}) — вероятно, бесконечная цепочка хуков; ` +
                'матч помечается ошибочным (tech/21 §5)',
    };
}
/** Прогнать НОВЫЙ итератор до первого yield/return; true ⇒ приостановлено. */
function driveIterator(s, iterator, initial) {
    let step = initial ?? iterator.next();
    for (;;) {
        if (step.done === true)
            return false;
        const outcome = suspendOn(s, iterator, step.value);
        if (outcome === 'pending')
            return true;
        if (outcome === 'match_error')
            return false;
        if (outcome === 'fizzle') {
            if (markAbilityBoundaryFizzled(step.value)) {
                // The compositor sees the private mutable flag before forwarding this
                // placeholder to its inner ability iterator, closes only that fragment,
                // and may yield the next fragment's request.
                const resumed = iterator.next([]);
                return resumed.done === true ? false : driveIterator(s, iterator, resumed);
            }
            // Raw hooks receive no exception and therefore cannot catch fizzle to
            // continue the same hook. Repeated return drains yields from finally blocks.
            let closed = iterator.return?.();
            while (closed !== undefined && closed.done !== true)
                closed = iterator.return?.();
            return false;
        }
        // skip: no PendingChoice, and the next runtime step is evaluated immediately.
        step = iterator.next([]);
    }
}
function driveFresh(s, iterator) {
    return driveIterator(s, iterator);
}
/** Completed appearances are transient resolution history, never GameState. */
function completedAppearanceIds(s) {
    return new Set(s.events.flatMap((event) => event.type === 'UnitAppeared' && event.outcome === 'survived'
        ? [event.instanceId]
        : []));
}
/** Follow the compact batch edge to its presentation root without trusting a cycle. */
function visualRootIndex(events, index) {
    let root = index;
    const visited = new Set();
    while (!visited.has(root)) {
        visited.add(root);
        const parent = events[root]?.causedByEventIndex;
        if (parent === undefined || !Number.isInteger(parent) || parent < 0 || parent >= root)
            break;
        root = parent;
    }
    return root;
}
/**
 * Combat keeps reducer facts deferred to preserve simultaneous rules.  Once
 * the FIFO stream reaches its late UnitDied/AttackResolved record we can
 * safely attach it to the already-public impact that caused it.  This changes
 * no rules ordering; it is only a compact visual edge for clients.
 */
function withDeferredVisualCause(events, event) {
    if (event.causedByEventIndex !== undefined)
        return event;
    if (event.type === 'UnitDied') {
        for (let index = events.length - 1; index >= 0; index -= 1) {
            const candidate = events[index];
            if (candidate.type !== 'DamageDealt'
                || candidate.lethal !== true
                || candidate.target.kind !== 'unit'
                || candidate.target.instanceId !== event.instanceId
                || (event.cause === 'combat' && candidate.combat !== true)
                || (event.cause === 'damage' && candidate.origin !== 'effect'))
                continue;
            return { ...event, causedByEventIndex: visualRootIndex(events, index) };
        }
    }
    if (event.type === 'AttackResolved' && event.attackGroupId !== undefined) {
        for (let index = events.length - 1; index >= 0; index -= 1) {
            const candidate = events[index];
            if (candidate.type !== 'DamageDealt' || candidate.attackGroupId !== event.attackGroupId)
                continue;
            return { ...event, causedByEventIndex: visualRootIndex(events, index) };
        }
    }
    return event;
}
/** Выполнить одного подписчика; true ⇒ его генератор приостановил резолюцию. */
function runSubscriber(s, sub, event, eventIndex) {
    if ('selfId' in sub.subject && s.draft.instances[sub.subject.selfId] === undefined)
        return false;
    if ('effectId' in sub.subject &&
        !s.draft.effects.some((effect) => effect.id === sub.subject.effectId)) {
        return false;
    }
    if ('attachmentId' in sub.subject &&
        !s.draft.attachments.some((attachment) => attachment.attachmentId === sub.subject.attachmentId))
        return false;
    const hooks = s.registry.get(sub.defId)?.hooks;
    if (hooks === undefined)
        return false;
    const previousCause = s.presentationCause.value;
    // Every result in a visually relevant reaction chain inherits its root.
    // Round upkeep and other routine lifecycle hooks keep their literal legacy
    // journal shape; the causal edge is reserved for facts that can be delayed
    // behind combat (and for their already-rooted descendants). Redaction then
    // remaps/drops the edge together with a non-public parent.
    const startsVisualChain = event.type === 'DamageDealt'
        || (event.type === 'UnitDied' && event.causedByEventIndex !== undefined)
        || (event.type === 'AttackResolved' && event.causedByEventIndex !== undefined)
        || event.type === 'CardsAbsorbed'
        || event.type === 'UnitHealed';
    s.presentationCause.value = startsVisualChain || event.causedByEventIndex !== undefined
        ? event.causedByEventIndex ?? eventIndex
        : undefined;
    try {
        const ctx = buildCardContext(s.draft, s.baseOps, sub.subject, sub.plan, s.registry, completedAppearanceIds(s));
        const body = invokeHookBody(hooks, sub.hookName, ctx, event);
        if (!isGenerator(body))
            return false;
        return driveFresh(s, body);
    }
    finally {
        s.presentationCause.value = previousCause;
    }
}
function invokeOrchestrationHook(s, hook) {
    if ('selfId' in hook.subject && s.draft.instances[hook.subject.selfId] === undefined)
        return undefined;
    if ('effectId' in hook.subject &&
        !s.draft.effects.some((effect) => effect.id === hook.subject.effectId))
        return undefined;
    if ('attachmentId' in hook.subject &&
        !s.draft.attachments.some((attachment) => attachment.attachmentId === hook.subject.attachmentId))
        return undefined;
    const hooks = s.registry.get(hook.defId)?.hooks;
    if (hooks === undefined)
        return undefined;
    const ctx = buildCardContext(s.draft, s.baseOps, hook.subject, hook.plan, s.registry, completedAppearanceIds(s));
    switch (hook.hookName) {
        case 'onPlay':
            return hooks.onPlay?.(ctx);
        case 'onBeforeCombat':
            return hooks.onBeforeCombat?.(ctx);
        case 'onAfterCombat':
            return hooks.onAfterCombat?.(ctx);
    }
}
/** Выполнить прямые фазовые хуки; true означает приостановку на choose. */
function runOrchestrationFrame(s) {
    const frame = s.orchestrationFrame;
    if (frame === null)
        return false;
    while (frame.index < frame.hooks.length) {
        const hook = frame.hooks[frame.index];
        frame.index += 1;
        if (hook === undefined)
            continue;
        const body = invokeOrchestrationHook(s, hook);
        if (isGenerator(body) && driveFresh(s, body))
            return true;
        if (s.terminalError !== null)
            return false;
    }
    s.orchestrationFrame = null;
    return false;
}
/** GC-sweep после кадра события, снявшего носителя/источник (D21/R8). */
function maybeSweep(s, event) {
    if (!isCarrierRemovingEvent(event))
        return;
    for (const effectId of sweepEffects(s.draft)) {
        // Снятие тем же путём ops → EffectRemoved в очередь → каскад хукам (§5).
        s.baseOps.removeEffect(effectId);
    }
}
/**
 * «Постоянно» (D53+): после каждого дренированного кадра движок один раз
 * реконсилирует производные ongoing-ауры из текущего snapshot (не подписка карты
 * на событие). Пропускается, если провайдеров нет, или пока активен combat-
 * deferral (снапшот боя ещё не физически завершён — реконсиляция после flush).
 */
function maybeReconcileOngoing(s) {
    if (!s.hasOngoing || s.queue.isDeferring)
        return;
    reconcileOngoing(s.draft, s.registry, s.baseOps);
}
function enqueueTokenAppearanceCompletion(s, snapshot) {
    const instance = s.draft.instances[snapshot.instanceId];
    const survived = instance !== undefined && isLiveUnit(s.draft, instance);
    const event = {
        type: 'UnitAppeared',
        ...snapshot,
        controllerId: instance?.controllerId ?? snapshot.ownerId,
        cause: 'token',
        outcome: survived ? 'survived' : 'fizzled',
    };
    s.pendingTokenAppearanceMarkers.add(event);
    s.queue.prepare(event);
    applyEvent(s.draft, event);
    s.queue.enqueue(event);
}
/**
 * Прокрутить резолюцию до завершения / ошибки лимита / приостановки на выбор.
 * Предусловие: s.active === null (нет висящего выбора).
 */
function pump(s) {
    for (;;) {
        let frame = s.frame;
        if (frame === null) {
            if (s.queue.isEmpty()) {
                if (s.orchestrationFrame !== null) {
                    if (runOrchestrationFrame(s))
                        return awaitingResult(s);
                    if (s.terminalError !== null)
                        return matchError(s, s.terminalError);
                    continue;
                }
                if (s.workflow !== null) {
                    if (s.events.length >= EVENT_LIMIT_PER_STEP)
                        return matchError(s);
                    const workflowCtx = {
                        draft: s.draft,
                        ops: s.baseOps,
                        emit(event, ownPlan) {
                            if (ownPlan !== undefined) {
                                s.ownPlansByEvent.set(event, structuredClone(ownPlan));
                            }
                            s.queue.prepare(event);
                            applyEvent(s.draft, event);
                            s.queue.enqueue(event);
                        },
                        scheduleHooks(hooks) {
                            if (s.orchestrationFrame !== null) {
                                throw new Error('resolution workflow: orchestration frame уже запланирован');
                            }
                            const sorted = [...hooks].sort(compareByCanonicalPlacement);
                            s.orchestrationFrame = { hooks: sorted, index: 0 };
                        },
                        beginEventDeferral() {
                            s.queue.beginDeferral();
                        },
                        flushDeferredEvents() {
                            s.queue.flushDeferred();
                        },
                    };
                    if (s.workflow.advance(workflowCtx)) {
                        s.workflow = null;
                        break;
                    }
                    continue;
                }
                break;
            }
            if (s.events.length >= EVENT_LIMIT_PER_STEP)
                return matchError(s);
            const queuedEvent = s.queue.dequeue();
            const event = withDeferredVisualCause(s.events, queuedEvent);
            s.events.push(event);
            const subs = s.subscriberSnapshots.get(queuedEvent) ?? [];
            s.subscriberSnapshots.delete(queuedEvent);
            const tokenAppearance = s.tokenAppearanceSnapshots.get(queuedEvent);
            s.tokenAppearanceSnapshots.delete(queuedEvent);
            const isTokenAppearanceMarker = s.pendingTokenAppearanceMarkers.delete(queuedEvent);
            const belongsToTokenAppearance = tokenAppearance !== undefined
                || isTokenAppearanceMarker
                || s.pendingTokenAppearanceMarkers.size > 0;
            frame = {
                event,
                eventIndex: s.events.length - 1,
                subs,
                index: 0,
                ...(belongsToTokenAppearance ? { appearanceTail: s.queue.captureTail() } : {}),
                ...(tokenAppearance !== undefined ? { tokenAppearance } : {}),
            };
            s.frame = frame;
        }
        let suspended = false;
        while (frame.index < frame.subs.length) {
            const sub = frame.subs[frame.index];
            frame.index += 1;
            if (sub === undefined)
                continue;
            if (runSubscriber(s, sub, frame.event, frame.eventIndex)) {
                suspended = true;
                break;
            }
            if (s.terminalError !== null)
                return matchError(s, s.terminalError);
        }
        if (suspended)
            return awaitingResult(s);
        maybeSweep(s, frame.event);
        maybeReconcileOngoing(s);
        if (frame.tokenAppearance !== undefined) {
            enqueueTokenAppearanceCompletion(s, frame.tokenAppearance);
        }
        if (frame.appearanceTail !== undefined) {
            s.queue.promoteSince(frame.appearanceTail);
        }
        s.frame = null;
    }
    finalizeDeadUnits(s.draft, s.events);
    return { status: 'completed', state: s.draft, events: s.events };
}
/** Начать возобновляемую фазовую машину M4 поверх копии состояния. */
export function startResolutionWorkflow(registry, state, workflow, initialEvents = []) {
    const draft = structuredClone(state);
    const queue = new EventQueue();
    const suspension = makeSuspension(registry, draft, queue, workflow);
    suspension.events.push(...initialEvents);
    return pump(suspension);
}
/**
 * Начать резолюцию с СИНТЕТИЧЕСКОЙ эмиссии события (tech-шов; в M4 — Action-apply):
 * копирует вход, применяет верхнее событие reducer'ом, кладёт в очередь и дренажит.
 */
export function startResolution(registry, state, event) {
    const draft = structuredClone(state);
    const queue = new EventQueue();
    const s = makeSuspension(registry, draft, queue);
    queue.prepare(event);
    applyEvent(draft, event);
    queue.enqueue(event);
    return pump(s);
}
/**
 * Начать резолюцию с тела-хука (белоящичный прогон): строит CardContext для
 * носителя, исполняет `body`, затем дренажит. Подписчики в дренаже могут быть
 * генераторами (choose) и приостанавливать резолюцию.
 */
export function runResolutionBody(registry, state, subject, body) {
    const draft = structuredClone(state);
    const queue = new EventQueue();
    const s = makeSuspension(registry, draft, queue);
    const ctx = buildCardContext(draft, s.baseOps, subject, undefined, s.registry, completedAppearanceIds(s));
    body(ctx);
    return pump(s);
}
/**
 * ВОЗОБНОВИТЬ резолюцию: валидирует selection по живому запросу, продолжает
 * генератор `.next(selection)` (ops ДО yield НЕ переисполняются), и — если тот же
 * генератор yield-нул следующий choose — снова приостанавливает (единственный
 * слот); иначе доигрывает кадр и дренажит остаток очереди С СОХРАНЁННОГО МЕСТА.
 */
export function resumeResolution(s, choiceId, selection) {
    const active = s.active;
    if (active === null) {
        throw new Error('resumeResolution: нет активного выбора (шаг не в состоянии awaiting_choice)');
    }
    if (active.choiceId !== choiceId) {
        throw new Error(`resumeResolution: choiceId "${choiceId}" не совпадает с ожидаемым "${active.choiceId}"`);
    }
    let generatorSelection;
    if (isTargetChooseRequest(active.request)) {
        if (!('tokens' in selection))
            throw new Error('Resolve: A3 choice требует selection.tokens');
        if (active.descriptor === undefined)
            throw new Error('Resolve: отсутствует transient choice descriptor');
        generatorSelection = resolveChoiceTokens(active.descriptor, active.request, selection.tokens, buildPublicTargetContext(s.draft, active.request.who, s.registry));
    }
    else {
        if ('tokens' in selection) {
            if (active.descriptor === undefined)
                throw new Error('Resolve: отсутствует legacy choice descriptor');
            if (new Set(selection.tokens).size !== selection.tokens.length) {
                throw new Error('Resolve: повтор choiceToken запрещён');
            }
            const selected = selection.tokens.map((token) => {
                const candidate = active.descriptor.targetByToken.get(token);
                if (candidate === undefined)
                    throw new Error(`Resolve: choiceToken "${token}" отсутствует`);
                if (candidate.kind !== 'unit' && candidate.kind !== 'card') {
                    throw new Error(`Resolve: legacy choice не поддерживает candidate kind "${candidate.kind}"`);
                }
                return candidate.instanceId;
            });
            const legacySelection = { picked: selected };
            // Tokens prove membership in the original materialized domain; the
            // legacy validator still re-runs the transient live filter on current state.
            validateSelection(s.draft, active.request, legacySelection);
            generatorSelection = legacySelection;
        }
        else {
            validateSelection(s.draft, active.request, selection);
            generatorSelection = selection;
        }
    }
    s.active = null;
    delete s.draft.pendingChoice;
    const previousCause = s.presentationCause.value;
    s.presentationCause.value = active.causeEventIndex;
    const step = active.iterator.next(generatorSelection);
    if (step.done !== true) {
        // Тот же генератор запросил СЛЕДУЮЩИЙ choose — снова приостановка (один слот).
        if (driveIterator(s, active.iterator, step)) {
            s.presentationCause.value = previousCause;
            return awaitingResult(s);
        }
        if (s.terminalError !== null) {
            s.presentationCause.value = previousCause;
            return matchError(s, s.terminalError);
        }
    }
    s.presentationCause.value = previousCause;
    return pump(s);
}
//# sourceMappingURL=resolution.js.map